// Puzzle identity: the YYYYMMDD integer for a given day in US Eastern time.
//
// This value is doing three jobs at once — it is the RNG seed for puzzle
// generation, the `puzzle_id` primary key in the database, and the archive
// URL parameter. It was previously recomputed inline in five places
// (App.js x3, SummaryBackside.js, and the backend), which is how the streak
// bug got in: the database returns `puzzle_id` as TEXT, and one of those
// call sites compared it against a number.
//
// Use `isSamePuzzleId` for any comparison against a value that came from the
// API. Never compare a raw `entry.puzzle_id` with `===`.

const TIME_ZONE = 'America/New_York';

// 'en-CA' formats as YYYY-MM-DD, which is why it's used here rather than
// the `new Date(d.toLocaleString('en-US', ...))` round-trip this replaces —
// that relied on the engine re-parsing a locale-formatted string.
const formatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Eastern calendar date for `date`, as { year, month, day } (month is 1-based). */
export const getEasternParts = (date = new Date()) => {
  const [year, month, day] = formatter.format(date).split('-').map(Number);
  return { year, month, day };
};

/** YYYYMMDD as a Number, e.g. 20260924. This is the generation seed. */
export const getPuzzleId = (date = new Date()) => {
  const { year, month, day } = getEasternParts(date);
  return year * 10000 + month * 100 + day;
};

/** Today's puzzle id, in Eastern time. */
export const todayPuzzleId = () => getPuzzleId();

/** YYYY-MM-DD for display and for the `date` query form. */
export const getPuzzleDateString = (date = new Date()) => formatter.format(date);

/**
 * Inverse of getPuzzleId: a puzzle id back to a Date, anchored at UTC noon
 * so that formatting it back in Eastern time cannot slip a day.
 * Used by the archive calendar and the next-puzzle countdown.
 */
export const getPuzzleDate = (puzzleId) => {
  const id = Number(puzzleId);
  if (!Number.isFinite(id)) return null;
  const year = Math.floor(id / 10000);
  const month = Math.floor((id % 10000) / 100);
  const day = id % 100;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
};

/** True if `value` is a well-formed YYYYMMDD id for a real calendar date. */
export const isValidPuzzleId = (value) => {
  if (!/^\d{8}$/.test(String(value))) return false;
  const date = getPuzzleDate(value);
  if (!date) return false;
  // Reject things like 20260231 that pass the range check but roll over.
  return getPuzzleId(date) === Number(value);
};

/**
 * Compare two puzzle ids that may be either Number or String.
 * `puzzle_id` is stored as TEXT, so anything from the API is a string.
 */
export const isSamePuzzleId = (a, b) =>
  a != null && b != null && String(a) === String(b);

/** Puzzle id for the day `offset` days from `from` (negative for the past). */
export const shiftPuzzleId = (puzzleId, offset) => {
  const date = getPuzzleDate(puzzleId);
  if (!date) return null;
  date.setUTCDate(date.getUTCDate() + offset);
  return getPuzzleId(date);
};

/**
 * The instant the next puzzle unlocks, i.e. the next Eastern midnight.
 *
 * Found by binary search on `getPuzzleId` rather than by arithmetic on a UTC
 * offset, because Eastern days are 23 or 25 hours long twice a year and the
 * offset itself changes mid-search. Costs ~17 Intl formats; call it once and
 * tick against the returned timestamp rather than per-second.
 */
export const getNextPuzzleTime = (now = new Date()) => {
  const currentId = getPuzzleId(now);
  let lo = now.getTime();
  let hi = lo + 48 * 60 * 60 * 1000; // any Eastern day boundary is well inside 48h
  while (hi - lo > 1000) {
    const mid = Math.floor((lo + hi) / 2);
    if (getPuzzleId(new Date(mid)) === currentId) lo = mid;
    else hi = mid;
  }
  return hi - (hi % 1000); // first whole second of the new day
};

/** Milliseconds until the next puzzle. */
export const msUntilNextPuzzle = (now = new Date()) =>
  Math.max(0, getNextPuzzleTime(now) - now.getTime());

const puzzleId = {
  getPuzzleId,
  todayPuzzleId,
  getPuzzleDate,
  getPuzzleDateString,
  getEasternParts,
  isValidPuzzleId,
  isSamePuzzleId,
  shiftPuzzleId,
  getNextPuzzleTime,
  msUntilNextPuzzle,
};

export default puzzleId;
